import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { DayStrip } from "@/components/DayStrip";
import { DeleteWeekButton } from "@/components/DeleteWeekButton";
import { WidgetRows } from "@/components/WidgetRows";
import { useWeek } from "@/hooks/useWeeks";
import { type DayKey, isDayKey, todayDayKeyInRange } from "@/lib/days";
import { setDayCompleted, updateWeek } from "@/lib/repos/weeks";
import type {
  DayWidgetWeekEntry,
  WeekData,
  WidgetWeekEntry,
} from "@/lib/schema";
import { getWidgetModule, isDayWidgetEntry } from "@/widgets/registry";

export function Day() {
  const params = useParams<{ weekId: string; dayKey?: string }>();
  const weekId = decodeURIComponent(params.weekId ?? "");
  const { week, loading } = useWeek(weekId);

  const dayKey = useMemo(() => {
    if (params.dayKey && isDayKey(params.dayKey)) return params.dayKey;
    return null;
  }, [params.dayKey]);

  if (loading) return <p>Laster…</p>;
  if (!week) return <p data-testid="week-not-found">Uken finnes ikke.</p>;

  if (!dayKey) {
    const inRange = todayDayKeyInRange(week.data.startDate, new Date());
    if (inRange) {
      return (
        <Navigate
          to={`/uke/${encodeURIComponent(weekId)}/${inRange}`}
          replace
        />
      );
    }
    return <Navigate to={`/uke/${encodeURIComponent(weekId)}`} replace />;
  }

  const isCompleted = week.data.completedDays?.[dayKey] === true;

  const handleToggleCompleted = async () => {
    await setDayCompleted(weekId, dayKey, !isCompleted);
  };

  return (
    <div data-screen-label={`Uke ${week.data.weekNumber} / ${dayKey}`}>
      <p className="week-cycle-label">
        Uke {week.data.weekNumber} · {week.data.year}
      </p>
      <DayStrip weekId={weekId} week={week.data} selected={dayKey} />
      <div className="page-toolbar">
        <button
          type="button"
          className={isCompleted ? "btn ghost" : "btn-primary"}
          onClick={handleToggleCompleted}
          data-testid="mark-completed-button"
        >
          {isCompleted ? "Fjern fullført" : "Marker dagen som fullført"}
        </button>
      </div>
      <WidgetGrid weekId={weekId} week={week.data} dayKey={dayKey} />
      <DeleteWeekButton weekId={weekId} />
    </div>
  );
}

interface GridProps {
  weekId: string;
  week: WeekData;
  dayKey: DayKey;
}

function WidgetGrid({ weekId, week, dayKey }: GridProps) {
  const dayEntries: DayWidgetWeekEntry[] = week.widgets
    .filter((w) => w.config.enabled && isDayWidgetEntry(w))
    .map((w) => w as DayWidgetWeekEntry);
  if (dayEntries.length === 0) {
    return (
      <p data-testid="day-empty">
        Ingen aktive widgets — gå til Innstillinger.
      </p>
    );
  }
  const visibleEntries = dayEntries.filter((w) => {
    const module = getWidgetModule(w.config.kind);
    if (module?.scope !== "day") return false;
    return module.isVisibleForDay
      ? module.isVisibleForDay(w.config, dayKey)
      : true;
  });
  const entryById = new Map(visibleEntries.map((e) => [e.config.id, e]));

  const handleChange = async (
    widgetId: string,
    target: "day" | "week",
    next: unknown,
  ) => {
    await updateWeek(weekId, (prev) => ({
      ...prev,
      widgets: prev.widgets.map((w): WidgetWeekEntry => {
        if (w.config.id !== widgetId) return w;
        if (!isDayWidgetEntry(w)) return w;
        if (target === "day") {
          return {
            ...w,
            days: {
              ...w.days,
              [dayKey]: next as DayWidgetWeekEntry["days"][DayKey],
            },
          };
        }
        return { ...w, week: next as DayWidgetWeekEntry["week"] };
      }),
    }));
  };

  return (
    <WidgetRows
      widgets={visibleEntries.map((e) => e.config)}
      renderWidget={(config) => {
        const entry = entryById.get(config.id);
        if (!entry) return null;
        const module = getWidgetModule(config.kind);
        if (module?.scope !== "day") return null;
        return (
          <module.DayView
            key={config.id}
            config={config}
            data={entry.days[dayKey]}
            weekData={entry.week}
            dayKey={dayKey}
            onChangeDay={(next: unknown) =>
              handleChange(config.id, "day", next)
            }
            onChangeWeek={(next: unknown) =>
              handleChange(config.id, "week", next)
            }
            weekId={weekId}
            week={week}
          />
        );
      }}
    />
  );
}
